import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { usePublicActivationPack } from "@/hooks/activation";
import { getMobilePlatform, type MobilePlatform } from "@/utils/device";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from "@/constants/appStore";
import { SITE_URL } from "@/config/site";
import {
  addPrivateAccessTokenFragment,
  capturePrivateAccessToken,
} from "@/lib/privateAccessToken";

// eslint-disable-next-line react-refresh/only-export-components -- exported for security-focused unit coverage.
export const appendTargetParams = (
  value: string,
  source: string | null,
  accessToken: string,
): string => {
  if (!source && !accessToken) return value;

  try {
    const url = new URL(value, SITE_URL);
    if (source) url.searchParams.set("source", source);
    if (
      url.origin === new URL(SITE_URL).origin &&
      url.pathname.startsWith("/live-agent/")
    ) {
      addPrivateAccessTokenFragment(url, accessToken);
    }
    return url.toString();
  } catch {
    return value;
  }
};

const getStoreUrl = (
  platform: MobilePlatform,
  source: string | null,
  packId?: string,
): string => {
  if (platform !== "android") return APP_STORE_URL;

  const referrer = new URLSearchParams({
    utm_source: source || "activation",
    utm_medium: "smart-link",
  });
  if (packId) referrer.set("pack_id", packId);

  const url = new URL(GOOGLE_PLAY_URL);
  url.searchParams.set("referrer", referrer.toString());
  return url.toString();
};

/**
 * Device-detecting interstitial behind the activation QR / app CTAs (`/get/:packId`).
 *
 * The default route keeps web chat primary and the app available alongside it.
 * `?intent=install` makes the relevant mobile store the primary action while
 * retaining web chat as a fallback. iOS Universal Links cover `/get/*`; add
 * Android `assetlinks.json` only after the production signing fingerprint is
 * available — a placeholder fingerprint would break verified App Links.
 */
export const SmartActivationLink = () => {
  const { packId } = useParams<{ packId: string }>();
  const [searchParams] = useSearchParams();
  const { data, isError, isFetching, isLoading, refetch } =
    usePublicActivationPack(packId);
  const platform = useMemo(() => getMobilePlatform(), []);
  const installIntent = searchParams.get("intent") === "install";
  const rawSource = searchParams.get("source")?.trim();
  const source = rawSource ? rawSource.slice(0, 100) : null;
  const accessToken = capturePrivateAccessToken() || "";
  const targetUrl = useMemo(
    () =>
      data?.targetUrl
        ? appendTargetParams(data.targetUrl, source, accessToken)
        : undefined,
    [accessToken, data?.targetUrl, source],
  );

  // Desktop only: no app to speak of, so skip the interstitial entirely.
  useEffect(() => {
    if (platform === "other" && targetUrl) {
      window.location.replace(targetUrl);
    }
  }, [platform, targetUrl]);

  const primary = data?.branding?.primaryColor || "#4E9CFF";
  const secondary = data?.branding?.secondaryColor || "#FF4DB8";
  const name = data?.name || "your Genie";
  const requestFailed = !isLoading && isError;
  const notFound = !packId || (!isLoading && !isError && data === null);
  const storeUrl = getStoreUrl(platform, source, packId);
  const storeBadgeSrc =
    platform === "android" ? GOOGLE_PLAY_BADGE_SRC : APP_STORE_BADGE_SRC;
  const storeBadgeAlt =
    platform === "android"
      ? "Get it on Google Play"
      : "Download on the App Store";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-center px-6"
      style={{
        background: `linear-gradient(135deg, ${primary}, ${secondary})`,
      }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>
          {installIntent
            ? `Install Help Genie for ${name}`
            : `Opening ${name}…`}
        </title>
      </Helmet>

      {requestFailed ? (
        <>
          <h1 className="text-white text-2xl font-bold">
            We couldn't open this link
          </h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            Something went wrong while loading it. Please try again.
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="mt-6 inline-flex items-center rounded-xl bg-white px-5 py-2.5 font-semibold disabled:opacity-60"
            style={{ color: primary }}
          >
            {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Try again
          </button>
        </>
      ) : !notFound ? (
        <>
          {platform === "other" ? (
            <>
              <Loader2 className="w-10 h-10 text-white animate-spin mb-5" />
              <h1 className="text-white text-xl font-semibold">
                Opening {name}…
              </h1>
              <p className="text-white/85 text-sm mt-2 max-w-sm">
                Taking you to the best place to start the conversation.
              </p>
            </>
          ) : installIntent ? (
            <>
              <h1 className="text-white text-2xl font-semibold">
                Keep {name} close
              </h1>
              <p className="text-white/85 text-sm mt-2 max-w-sm">
                Install Help Genie on your phone, or keep talking in your
                browser.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-white text-2xl font-semibold">
                Talk to {name}
              </h1>
              <p className="text-white/85 text-sm mt-2 max-w-sm">
                Start now in your browser or keep {name} handy in the app.
              </p>
            </>
          )}
          <div className="mt-8 flex flex-col items-center gap-4">
            {platform !== "other" && installIntent && (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={storeBadgeAlt}
                className="inline-flex rounded-xl bg-white px-5 py-4 shadow-lg hover:scale-105 transition-transform"
              >
                <img src={storeBadgeSrc} alt={storeBadgeAlt} className="h-11" />
              </a>
            )}
            {platform !== "other" && targetUrl && (
              <a
                href={targetUrl}
                className={
                  installIntent
                    ? "text-white underline text-sm font-medium"
                    : "px-6 py-3 rounded-xl bg-white font-semibold shadow-lg hover:scale-105 transition-transform"
                }
                style={installIntent ? undefined : { color: primary }}
              >
                {installIntent
                  ? `Talk to ${name} in your browser instead`
                  : `Talk to ${name} now`}
              </a>
            )}
            {platform === "other" && targetUrl && (
              <a
                href={targetUrl}
                className="text-white underline text-sm font-medium"
              >
                Continue in your browser
              </a>
            )}
            {platform !== "other" && !installIntent && (
              <div className="flex flex-col items-center gap-2">
                <p className="text-white/85 text-xs">
                  Keep the conversation close
                </p>
                <a
                  href={storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={storeBadgeAlt}
                  className="hover:scale-105 transition-transform"
                >
                  <img
                    src={storeBadgeSrc}
                    alt={storeBadgeAlt}
                    className="h-10"
                  />
                </a>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <h1 className="text-white text-2xl font-bold">
            This link isn't available
          </h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            The Genie you're looking for may be private or no longer active.
          </p>
          <a
            href="/"
            className="mt-6 inline-flex items-center px-5 py-2.5 rounded-xl bg-white font-semibold"
            style={{ color: primary }}
          >
            Go to Help Genie
          </a>
        </>
      )}
    </div>
  );
};

export default SmartActivationLink;
